import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { BaseModalProps, Button, ModalContentWrapper, TextInput } from 'ui';

interface EditSecretModalProps extends BaseModalProps {
  vaultName: string;
  secretName: string;
  secretValue: string;
  onSave: (payload: { name: string; value: string }) => Promise<void>;
}

const EditSecretModal = ({
  open,
  onClose,
  onSave,
  vaultName,
  secretName,
  secretValue,
}: EditSecretModalProps) => {
  const [name, setName] = useState(secretName);
  const [value, setValue] = useState(secretValue);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setName(secretName);
    setValue(secretValue);
  }, [secretName, secretValue]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim() || !value.trim()) {
      toast.error('Secret name and value are required');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSave({ name: name.trim(), value });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalContentWrapper
      open={open}
      title={`Edit Secret in ${vaultName}`}
      onClose={handleClose}
      variant="center"
      classes={{ dialogPanel: 'max-w-xl' }}
    >
      <div className="px-6 pb-6 pt-2 space-y-4">
        <TextInput
          name="secret_name"
          label="Secret name"
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <TextInput
          name="secret_value"
          label="Secret value"
          textarea
          value={value}
          onChange={e => setValue(e.target.value)}
          className="min-h-[150px]"
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </div>
    </ModalContentWrapper>
  );
};

export default EditSecretModal;
